import { useAdminSubmissionContext } from '@/providers/AdminSubmissionProvider/AdminSubmissionContext';
import { propertyChanged } from '@/providers/AdminSubmissionProvider/actions';
import { Input } from '@/components/ui/input';
import {
  SettingRowCell,
  SettingRowInput,
  SettingsRow,
} from '@/components/Section';
import {
  Maybe,
  SlideImageTilt,
  SlidesSubmissionData,
} from '@/generated/graphql';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CameraIcon, DotsVerticalIcon, TrashIcon } from '@radix-ui/react-icons';
import { CropAndUploadDialog } from '@/components/CropAndUploadDialog';
import { InlineConfirmButton } from '@/components/InlineConfirmButton';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { normalizeSlideImageTilt } from '../slideLayout';

export function EditableSlideRow({
  onDelete,
  currentSlideId,
  onSetCurrentSlideId,
}: {
  onDelete: (id: string) => void;
  currentSlideId: string | null;
  onSetCurrentSlideId: (id: string) => void;
}) {
  const { state, dispatch, submission } =
    useAdminSubmissionContext<SlidesSubmissionData>();

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: submission.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SettingsRow ref={setNodeRef} style={style} forceRow>
      <SettingRowCell>
        <div
          className="w-10 text-center text-lg text-muted-foreground hover:cursor-grab hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <DotsVerticalIcon className="h-6 w-6" />
        </div>
      </SettingRowCell>
      <SettingRowCell>
        <Switch
          checked={currentSlideId === submission.id}
          onCheckedChange={() => onSetCurrentSlideId(submission.id)}
        />
      </SettingRowCell>
      <SettingRowInput className="flex flex-col gap-2">
        <Input
          id={`title-${submission.id}`}
          placeholder="Title"
          value={state.title}
          onChange={(e) => dispatch(propertyChanged('title', e.target.value))}
        />
        <Textarea
          id={`content-${submission.id}`}
          placeholder="Content (supports markdown)"
          value={state.content}
          rows={5}
          onChange={(e) => dispatch(propertyChanged('content', e.target.value))}
        />
      </SettingRowInput>
      <SettingRowCell>
        <div className="flex flex-col items-center gap-2">
          <OptionImageInput
            imageUrl={state.optionalImageUrl}
            onChange={(imageUrl) =>
              dispatch(propertyChanged('optionalImageUrl', imageUrl))
            }
          />
          <div className="flex w-32 flex-col gap-1">
            <Label
              htmlFor={`image-tilt-${submission.id}`}
              className="text-xs text-muted-foreground"
            >
              Image tilt
            </Label>
            <Select
              value={normalizeSlideImageTilt(state.imageTilt)}
              onValueChange={(next) =>
                dispatch(propertyChanged('imageTilt', next as SlideImageTilt))
              }
            >
              <SelectTrigger
                id={`image-tilt-${submission.id}`}
                data-name="SLIDE_IMAGE_TILT"
                className="h-8"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SlideImageTilt.Left}>Left</SelectItem>
                <SelectItem value={SlideImageTilt.Right}>Right</SelectItem>
                <SelectItem value={SlideImageTilt.None}>No tilt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </SettingRowCell>
      <SettingRowCell>
        <InlineConfirmButton
          variant="ghost"
          size="icon"
          onConfirm={() => onDelete(submission.id)}
        >
          <TrashIcon />
        </InlineConfirmButton>
      </SettingRowCell>
    </SettingsRow>
  );
}

function OptionImageInput({
  imageUrl,
  onChange,
}: {
  imageUrl: Maybe<string> | undefined;
  onChange: (imageUrl: string) => void;
}) {
  return (
    <CropAndUploadDialog
      ratio={1}
      onSave={onChange}
      maxWidth={1080}
      maxHeight={1080}
    >
      <div className="flex h-16 w-16 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 bg-muted">
        {imageUrl ? (
          <img src={imageUrl} className="h-16 w-16" />
        ) : (
          <CameraIcon className="h-6 w-6" />
        )}
      </div>
    </CropAndUploadDialog>
  );
}
